"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type ScrollState = { left: boolean; right: boolean; hiddenColumns: number };

const NO_OVERFLOW: ScrollState = { left: false, right: false, hiddenColumns: 0 };

// A few pixels of slack so sub-pixel layout never reads as "more to scroll".
const EDGE_SLACK_PX = 4;

export interface ScrollHintAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Classes for the element that actually scrolls (previously the table container). */
  scrollClassName?: string;
}

/**
 * A horizontally scrollable region that says so.
 *
 * Wide tables used to scroll silently: the scrollbar sits at the bottom of the
 * table, below the fold on any long list, and macOS hides it until you scroll,
 * so users never learned there were more columns. When (and only when) the
 * content is wider than the box, this adds a bar above it naming how many
 * columns are out of view, with buttons to page across, and a fade on each
 * edge that has hidden content. Content that fits renders exactly as before.
 */
export const ScrollHintArea = React.forwardRef<HTMLDivElement, ScrollHintAreaProps>(
  ({ className, scrollClassName, children, ...props }, forwardedRef) => {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const frameRef = React.useRef(0);
    const observerRef = React.useRef<ResizeObserver | null>(null);
    const observedContentRef = React.useRef<Element | null>(null);
    const [state, setState] = React.useState<ScrollState>(NO_OVERFLOW);

    const setScrollNode = React.useCallback(
      (node: HTMLDivElement | null) => {
        scrollRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const measure = React.useCallback(() => {
      const node = scrollRef.current;
      if (!node) return;
      // The table can mount or be replaced after first render (data arriving),
      // so follow whichever element is the content now.
      const content = node.firstElementChild;
      if (observerRef.current && content !== observedContentRef.current) {
        if (observedContentRef.current) observerRef.current.unobserve(observedContentRef.current);
        if (content) observerRef.current.observe(content);
        observedContentRef.current = content;
      }
      const maxScroll = node.scrollWidth - node.clientWidth;
      const left = node.scrollLeft > EDGE_SLACK_PX;
      const right = maxScroll - node.scrollLeft > EDGE_SLACK_PX;
      let hiddenColumns = 0;
      if (right) {
        const edge = node.getBoundingClientRect().right - EDGE_SLACK_PX;
        node.querySelectorAll("thead th").forEach((cell) => {
          if (cell.getBoundingClientRect().left >= edge) hiddenColumns += 1;
        });
      }
      // Only re-render when something the user can see actually changed.
      setState((previous) =>
        previous.left === left && previous.right === right && previous.hiddenColumns === hiddenColumns
          ? previous
          : { left, right, hiddenColumns },
      );
    }, []);

    const scheduleMeasure = React.useCallback(() => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(measure);
    }, [measure]);

    React.useEffect(() => {
      const node = scrollRef.current;
      if (!node) return;
      // Container and content both change width on their own (sidebar
      // collapse, data arriving, fonts loading), not only on window resize.
      const observer = new ResizeObserver(scheduleMeasure);
      observerRef.current = observer;
      observer.observe(node);
      // A table swapped in for a same-height placeholder (skeleton, empty
      // state) changes no size, so watch the direct children as well.
      const childWatcher = new MutationObserver(scheduleMeasure);
      childWatcher.observe(node, { childList: true });
      measure();
      node.addEventListener("scroll", scheduleMeasure, { passive: true });
      return () => {
        node.removeEventListener("scroll", scheduleMeasure);
        childWatcher.disconnect();
        observer.disconnect();
        observerRef.current = null;
        observedContentRef.current = null;
        cancelAnimationFrame(frameRef.current);
      };
    }, [measure, scheduleMeasure]);

    const page = (direction: -1 | 1) => {
      const node = scrollRef.current;
      if (!node) return;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      node.scrollBy({
        left: direction * Math.max(240, Math.round(node.clientWidth * 0.8)),
        behavior: reduceMotion ? "auto" : "smooth",
      });
    };

    const overflowing = state.left || state.right;
    const hint = !state.right
      ? "Showing the last columns"
      : state.hiddenColumns > 0
        ? `${state.hiddenColumns} more column${state.hiddenColumns === 1 ? "" : "s"} to the right`
        : "More columns to the right";

    return (
      <div className={cn("relative w-full min-w-0 max-w-full", className)} {...props}>
        {overflowing && (
          <div className="flex items-center justify-end gap-2 pb-2 print:hidden">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300" aria-live="polite">
              {hint}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => page(-1)}
              disabled={!state.left}
              aria-label="Scroll table left"
              title="Scroll left"
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => page(1)}
              disabled={!state.right}
              aria-label="Scroll table right"
              title="Scroll right"
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
        <div className="relative">
          <div ref={setScrollNode} className={scrollClassName}>
            {children}
          </div>
          {state.left && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-px left-px z-20 w-10 rounded-l-md bg-gradient-to-r from-slate-900/15 to-transparent print:hidden dark:from-black/40"
            />
          )}
          {state.right && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-px right-px z-20 w-10 rounded-r-md bg-gradient-to-l from-slate-900/15 to-transparent print:hidden dark:from-black/40"
            />
          )}
        </div>
      </div>
    );
  },
);
ScrollHintArea.displayName = "ScrollHintArea";
